/**
 * ExpandedInvitationList Component
 * Full expanded view with input, scrollable list, and close button
 */

import { memo } from 'react';
import { Stack, Button, Text, Group } from '@mantine/core';
import { IconUsers } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { AppScrollArea } from '@/components/ui/AppScrollArea';
import { InvitationInput } from '../InvitationInput';
import { InvitationListItem } from '../InvitationListItem';
import {
  FONT_WEIGHT_SEMIBOLD,
  INVITATION_LIST_MAX_HEIGHT,
} from '@/constants/ui';
import { INVITATION_BATCH_MAX_SIZE } from '@/constants/workspace';
import type { ExpandedInvitationListProps } from '../../types';
import styles from './ExpandedInvitationList.module.css';

export const ExpandedInvitationList = memo(function ExpandedInvitationList({
  invitations,
  onAdd,
  onRemove,
  onRoleChange,
  onClose,
  isSubmitting = false,
}: ExpandedInvitationListProps) {
  const { t } = useTranslation();
  const isAtLimit = invitations.length >= INVITATION_BATCH_MAX_SIZE;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div>
          <Text fw={FONT_WEIGHT_SEMIBOLD}>
            {t('workspace.form.invitationsTitle')}
          </Text>
          <Text size="sm" c="dimmed">
            {t('workspace.form.invitationsDescription')}
          </Text>
        </div>

        <InvitationInput
          onAdd={onAdd}
          pendingInvitations={invitations}
          isSubmitting={isSubmitting}
          isAtLimit={isAtLimit}
        />

        {invitations.length > 0 ? (
          <AppScrollArea maxHeight={INVITATION_LIST_MAX_HEIGHT} clampWidth>
            <Stack gap="xs">
              {invitations.map((invitation) => (
                <InvitationListItem
                  key={invitation.id}
                  invitation={invitation}
                  onRemove={onRemove}
                  onRoleChange={onRoleChange}
                  disabled={isSubmitting}
                />
              ))}
            </Stack>
          </AppScrollArea>
        ) : (
          <Group gap="xs" c="dimmed" className={styles.emptyState}>
            <IconUsers size={16} />
            <Text size="sm">{t('workspace.form.emptyHint')}</Text>
          </Group>
        )}
      </div>

      {/* Close button - always at bottom */}
      <Button variant="subtle" onClick={onClose} className={styles.closeButton}>
        {t('workspace.invitations.close')}
      </Button>
    </div>
  );
});
